import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Skeleton } from '../components/ui/skeleton';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { 
  Users, ChevronDown, Star, Briefcase, TrendingUp, 
  Target, Sparkles, Crown
} from 'lucide-react';

const ClusterIcon = ({ name }) => {
  const icons = {
    "Senior Experts": Crown,
    "Mid-Level Strong": Briefcase,
    "Rising Stars": TrendingUp,
    "Domain Specialists": Target,
    "Versatile Generalists": Sparkles
  };
  const Icon = icons[name] || Users;
  return <Icon className="h-4 w-4" strokeWidth={1.5} />;
};

const ClusterCard = ({ cluster, isOpen, onToggle }) => {
  const getClusterColor = (name) => {
    const colors = {
      "Senior Experts": "bg-purple-500/10 text-purple-500 border-purple-500/20",
      "Mid-Level Strong": "bg-blue-500/10 text-blue-500 border-blue-500/20",
      "Rising Stars": "bg-green-500/10 text-green-500 border-green-500/20",
      "Domain Specialists": "bg-orange-500/10 text-orange-500 border-orange-500/20",
      "Versatile Generalists": "bg-slate-500/10 text-slate-500 border-slate-500/20"
    };
    return colors[name] || "bg-muted text-muted-foreground";
  };

  return (
    <Collapsible open={isOpen} onOpenChange={onToggle}>
      <Card className={`transition-all ${isOpen ? 'border-primary/30' : ''}`}>
        <CollapsibleTrigger asChild>
          <CardHeader className="pb-2 cursor-pointer hover:bg-muted/30 transition-colors rounded-t-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${getClusterColor(cluster.name)}`}>
                  <ClusterIcon name={cluster.name} />
                </div>
                <div>
                  <h3 className="font-semibold text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {cluster.name}
                  </h3>
                  <p className="text-xs text-muted-foreground">{cluster.description}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="text-xs gap-1">
                  <Users className="h-3 w-3" />
                  {cluster.count}
                </Badge>
                <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
              </div>
            </div>
          </CardHeader>
        </CollapsibleTrigger>
        
        <CollapsibleContent>
          <CardContent className="pt-0">
            <div className="space-y-2 mt-2">
              {cluster.candidates.map((candidate, idx) => (
                <div 
                  key={candidate.id}
                  className="flex items-center justify-between p-2 rounded-md bg-muted/50 hover:bg-muted transition-colors"
                >
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <span className="text-xs text-muted-foreground w-5">#{idx + 1}</span>
                    <div className="min-w-0">
                      <p className="font-medium text-sm truncate">{candidate.name}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {candidate.experience_years || '?'} yrs • {candidate.current_company || 'Unknown'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge 
                      className={`text-xs ${
                        candidate.fit_label === 'Strong Fit' 
                          ? 'bg-green-500/10 text-green-500 border-green-500/20'
                          : candidate.fit_label === 'Good Fit'
                          ? 'bg-blue-500/10 text-blue-500 border-blue-500/20'
                          : 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20'
                      }`}
                    >
                      {Math.round(candidate.final_score || 0)}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
};

export const CandidateClusters = ({ clusters, isLoading }) => {
  const [openCluster, setOpenCluster] = React.useState(null);

  if (isLoading) {
    return (
      <Card data-testid="clusters-loading">
        <CardHeader className="pb-3">
          <Skeleton className="h-6 w-44" />
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <Skeleton key={i} className="h-20" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!clusters || clusters.length === 0) {
    return (
      <Card data-testid="clusters-empty">
        <CardContent className="py-8 text-center text-muted-foreground">
          <Users className="h-10 w-10 mx-auto mb-3 opacity-50" strokeWidth={1.5} />
          <p className="text-sm">No clusters available</p>
          <p className="text-xs mt-1">Need at least 2 analyzed candidates for clustering</p>
        </CardContent>
      </Card>
    );
  }

  const totalCandidates = clusters.reduce((sum, c) => sum + c.count, 0);

  return (
    <Card data-testid="candidate-clusters">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <Sparkles className="h-4 w-4 text-primary" strokeWidth={1.5} />
            Similarity Clusters
          </CardTitle>
          <Badge variant="secondary" className="text-xs">
            {totalCandidates} Candidates
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <ScrollArea className="h-[350px] pr-2">
          <div className="space-y-3">
            {clusters.map((cluster, idx) => (
              <ClusterCard 
                key={idx}
                cluster={cluster}
                isOpen={openCluster === idx}
                onToggle={() => setOpenCluster(openCluster === idx ? null : idx)}
              />
            ))}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
};

export default CandidateClusters;
